import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";

function fmt(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function signalLabel(type) {
  if (type === "concurrent_interview_blocked") return "Concurrent interview blocked";
  if (type === "simultaneous_login_blocked") return "Simultaneous login blocked";
  if (type === "session_takeover_by_verified_login") return "Verified session takeover";
  return type || "Account integrity signal";
}

const RISK_STYLE = {
  normal: "bg-emerald-50 border-emerald-200 text-emerald-800",
  review: "bg-amber-50 border-amber-200 text-amber-900",
  repeated: "bg-orange-50 border-orange-200 text-orange-900",
  persistent: "bg-rose-50 border-rose-200 text-rose-900",
};

export function AdminAccountSharingAlerts({ onSelectUser }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/account-sharing-alerts");
      setData(res.data || { alerts: [], total: 0 });
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const alerts = data?.alerts || [];
  return (
    <div className="mb-4 rounded-xl border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] p-3" data-testid="admin-account-sharing-alerts">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">Account-sharing review</div>
          <div className="mt-0.5 text-sm font-semibold">{loading ? "Checking login patterns…" : `${alerts.length} account${alerts.length === 1 ? "" : "s"} flagged`}</div>
          <div className="mt-1 text-[11px] leading-relaxed text-[color:var(--text-secondary)]">Review flag: 3+ distinct successful-login IPs in a rolling 24-hour period. Manual review only — no automatic warning or suspension.</div>
        </div>
        <button type="button" onClick={load} className="shrink-0 rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-[11px] hover:bg-white">Refresh</button>
      </div>

      {alerts.length > 0 && (
        <div className="mt-3 space-y-2">
          {alerts.map((a) => (
            <button key={a.user_id} type="button" onClick={() => onSelectUser?.(a.user_id)} className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-left text-xs hover:bg-amber-50" data-testid={`account-sharing-alert-${a.user_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium truncate">{a.email || a.user_id}</span>
                <span className={`rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-widest ${RISK_STYLE[a.risk_level] || RISK_STYLE.review}`}>{a.risk_level}</span>
              </div>
              <div className="mt-1 text-[10px] text-[color:var(--text-secondary)]">{a.distinct_ips_24h} IPs / 24h · {a.flagged_days_7d} flagged day(s) / 7d · {a.simultaneous_login_blocks_14d || 0} login block(s) · {a.session_takeovers_14d || 0} takeover(s)</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminLoginRiskPanel({ userId }) {
  const [data, setData] = useState(null);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [riskRes, contactRes] = await Promise.all([
        api.get(`/admin/users/${userId}/login-risk`),
        api.get(`/admin/users/${userId}/contact`),
      ]);
      setData(riskRes.data || null);
      setContact(contactRes.data?.contact || null);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex));
      setData(null);
      setContact(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (!userId) return null;
  if (loading && !data) return <div className="text-xs text-[color:var(--text-secondary)]">Loading account review…</div>;
  if (!data) return null;

  const events = data.login_events || [];
  const signals = data.signals || [];
  const address = contact
    ? [contact.street_address, contact.city, contact.region, contact.postal_code, contact.country].filter(Boolean).join(", ")
    : "—";

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[color:var(--border-stone)] p-4" data-testid="admin-user-contact-profile">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">Account contact record</div>
        <div className="mt-1 text-sm font-semibold">Identity and contact information</div>
        <div className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
          <ContactField label="Name" value={contact ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—" : "—"} />
          <ContactField label="Email" value={contact?.email || "—"} />
          <ContactField label="Telephone" value={contact?.telephone || "—"} />
          <ContactField label="Address" value={address} />
          <ContactField label="18+ confirmed" value={contact?.age_18_or_older ? "Yes" : "No / legacy account"} />
          <ContactField label="Age confirmation recorded" value={fmt(contact?.age_confirmed_at)} />
        </div>
        <div className="mt-3 rounded-lg bg-[color:var(--bg-surface-hover)] px-3 py-2 text-[11px] text-[color:var(--text-secondary)]">Contact information is for account administration, ownership verification, service communication, and security matters. It is not used in interview prompts or reports.</div>
      </div>

      <div className="rounded-xl border border-[color:var(--border-stone)] p-4" data-testid="admin-user-login-risk">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">Login pattern review</div>
            <div className="mt-1 text-sm font-semibold">Account-sharing indicators</div>
          </div>
          <span className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-widest ${RISK_STYLE[data.risk_level] || RISK_STYLE.normal}`}>{data.risk_level}</span>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-3 xl:grid-cols-6">
          <Metric label="IPs / 24h" value={`${data.distinct_ips_24h} / ${data.ip_threshold}`} />
          <Metric label="Flagged days / 7d" value={data.flagged_days_7d} />
          <Metric label="Longest streak" value={`${data.max_consecutive_flagged_days_7d} day(s)`} />
          <Metric label="Login blocks / 14d" value={data.simultaneous_login_blocks_14d || 0} />
          <Metric label="Takeovers / 14d" value={data.session_takeovers_14d || 0} />
          <Metric label="Interview blocks / 14d" value={data.concurrent_interview_blocks_14d || 0} />
        </div>

        <div className="mt-3 rounded-lg bg-[color:var(--bg-surface-hover)] px-3 py-2 text-[11px] leading-relaxed text-[color:var(--text-secondary)]">An IP flag or session event is an internal review signal, not proof of account sharing. VPNs, mobile networks, closed browsers and normal device changes can create legitimate events. No automatic user warning or account action is taken.</div>

        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">Recent successful logins ({events.length})</div>
          {events.length === 0 ? (
            <div className="mt-1 text-xs text-[color:var(--text-secondary)]">No recorded login events yet.</div>
          ) : (
            <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-[color:var(--border-stone)]">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-white text-[9px] uppercase tracking-widest text-[color:var(--text-secondary)]">
                  <tr><th className="px-2 py-2 text-left">When</th><th className="px-2 py-2 text-left">IP</th><th className="px-2 py-2 text-left">Device</th><th className="px-2 py-2 text-left">Auth</th></tr>
                </thead>
                <tbody>
                  {events.map((e, i) => (
                    <tr key={`${e.session_fingerprint || i}-${i}`} className="border-t border-[color:var(--border-stone)]">
                      <td className="px-2 py-1.5 whitespace-nowrap">{fmt(e.at)}</td><td className="px-2 py-1.5 font-mono whitespace-nowrap">{e.ip || "unknown"}</td><td className="px-2 py-1.5">{e.device || "—"}</td><td className="px-2 py-1.5">{e.auth_provider || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {signals.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">Account integrity signals</div>
            <div className="mt-2 space-y-1">
              {signals.map((s, i) => (
                <div key={`${s.type}-${i}`} className="rounded-lg border border-[color:var(--border-stone)] px-3 py-2 text-[11px]">
                  <span className="font-medium">{signalLabel(s.type)}</span><span className="ml-2 text-[color:var(--text-secondary)]">{fmt(s.at)} · {s.ip || "unknown"} · {s.device || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-3 flex justify-end"><button type="button" onClick={load} className="rounded-full border border-[color:var(--border-stone)] px-3 py-1 text-[11px] hover:bg-[color:var(--bg-surface-hover)]">Refresh account review</button></div>
      </div>
    </div>
  );
}

function ContactField({ label, value }) {
  return <div><div className="text-[9px] uppercase tracking-widest text-[color:var(--text-secondary)]">{label}</div><div className="mt-0.5 text-sm font-medium break-words">{value}</div></div>;
}

function Metric({ label, value }) {
  return <div><div className="text-[9px] uppercase tracking-widest text-[color:var(--text-secondary)]">{label}</div><div className="mt-0.5 text-sm font-semibold">{value}</div></div>;
}
